import type { SubmitPayload } from "./state.ts";
import type { SubmitResponseBody } from "./types.ts";

export interface SubmitOptions {
  apiBase: string;
  fetchImpl?: typeof fetch;
}

export const SUBMIT_REJECTED_MESSAGE =
  "Your answers couldn't be saved because something went wrong on our end. Reload the page and try again.";

export const SECURITY_CHECK_MESSAGE = "The security check didn't go through. Please complete the check below and try again.";
export const GEO_BLOCK_MESSAGE = "Submissions are not accepted from the EU, EEA, or UK.";
// A failed submit always spends the Turnstile token (main.ts clears it and the
// widget is remounted), so a retry after a 429 needs a fresh check too.
export const RATE_LIMIT_MESSAGE = "Too many submissions from your IP address. Please wait a minute, complete the check below, and try again.";
export const NETWORK_ERROR_MESSAGE = "Your answers couldn't be sent. Check your connection and try again.";

export class SubmitError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "SubmitError";
  }
}

/** Visitor-facing text for any error thrown while submitting. Never exposes raw exception text. */
export function submitErrorMessage(err: unknown): string {
  if (err instanceof SubmitError) {
    if (err.status === 403) return SECURITY_CHECK_MESSAGE;
    if (err.status === 451) return GEO_BLOCK_MESSAGE;
    if (err.status === 429) return RATE_LIMIT_MESSAGE;
    if (err.status === 400) return SUBMIT_REJECTED_MESSAGE;
  }
  return NETWORK_ERROR_MESSAGE;
}

export async function submitResult(payload: SubmitPayload, opts: SubmitOptions): Promise<SubmitResponseBody> {
  const doFetch = opts.fetchImpl ?? fetch;

  const response = await doFetch(`${opts.apiBase}/api/submit`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });

  const body = await response.json();

  if (!response.ok) {
    if (response.status === 400) throw new SubmitError(SUBMIT_REJECTED_MESSAGE, 400);
    const message = typeof body?.error === "string" ? body.error : `Submission failed (HTTP ${response.status}).`;
    throw new SubmitError(message, response.status);
  }

  return body as SubmitResponseBody;
}
